/**
 * BlowerSelectionPage.tsx — Blower selection & VSD turn-down matching
 *
 * Closes the gap between "we need X Nm³/h of air" and "this blower with VSD
 * is the right unit". Takes the seasonal AOTR demand curve and matches it
 * against typical VSD blower performance curves with N+1 redundancy.
 *
 * Tracks:
 *   - Per-season AOTR demand (winter / spring / summer / autumn / worst)
 *   - Corresponding airflow (Nm³/h)
 *   - Required blower power (isentropic + efficiency)
 *   - Match against 3 candidate blower families (lobe / screw / turbo)
 *   - N+1 redundancy and turn-down adequacy
 *
 * References:
 *   ASHRAE (2020). Handbook of HVAC Applications, Ch. 41.
 *   Atlas Copco / Aerzen / Howden product literature (typical performance bands).
 *   Bell & Abel (2008). Energy-efficient operation of biological aeration.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Wind, ArrowLeft, RefreshCcw, CheckCircle2, AlertTriangle } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import { specFromLegacy, type LegacyModSpec, qPerModuleNm3h } from '@/lib/diffuserPhysics';

const G = 9.81;
const P_ATM = 101_325;
const RHO_W = 1000;

interface ModSpec extends LegacyModSpec {
  waterDepthM: number;
  mountingHeightM: number;
  waterTempC: number;
  diffuserDropKpa?: number;
}

const DEFAULTS: ModSpec = {
  diffuserCount: 8,
  diffuserType: 'membrane_disc_epdm',
  diffuserDiamMm: 300,
  poreSizeUm: 80,
  airflowPerDiffNm3h: 1.2,
  waterDepthM: 3.0,
  mountingHeightM: 0.40,
  waterTempC: 12,
  diffuserDropKpa: 1.5,
};

interface SeasonalDemand {
  label: string;
  modules: number;
  factor: number;   // demand factor vs nominal
}

const DEFAULT_DEMAND: SeasonalDemand[] = [
  { label: 'Winter',       modules: 12, factor: 0.55 },
  { label: 'Spring',       modules: 16, factor: 0.85 },
  { label: 'Summer',       modules: 20, factor: 1.00 },
  { label: 'Autumn',       modules: 18, factor: 0.90 },
  { label: 'Worst case',   modules: 22, factor: 1.15 },
];

interface BlowerCandidate {
  id: string;
  label: string;
  technology: 'lobe' | 'screw' | 'turbo';
  qMin_Nm3h: number;
  qMax_Nm3h: number;
  turnDownRatio: number;
  efficiency: number;     // overall wire-to-air
  noise_dBA: number;
  capexBand: 'low' | 'mid' | 'high';
  notes: string;
}

const BLOWERS: BlowerCandidate[] = [
  { id: 'lobe', label: 'Roots lobe (Aerzen GM/Atlas ZB)', technology: 'lobe',
    qMin_Nm3h: 80, qMax_Nm3h: 500, turnDownRatio: 1.8, efficiency: 0.58, noise_dBA: 78,
    capexBand: 'low', notes: 'Simple, robust, widely available. Lower efficiency, higher noise.' },
  { id: 'screw', label: 'Hybrid screw (Aerzen Delta Hybrid)', technology: 'screw',
    qMin_Nm3h: 60, qMax_Nm3h: 800, turnDownRatio: 4.0, efficiency: 0.72, noise_dBA: 72,
    capexBand: 'mid', notes: 'Excellent turn-down, higher efficiency, mid CAPEX. Sweet spot for variable demand.' },
  { id: 'turbo', label: 'Magnetic-bearing turbo (Sulzer HST / Howden)', technology: 'turbo',
    qMin_Nm3h: 200, qMax_Nm3h: 3000, turnDownRatio: 2.5, efficiency: 0.80, noise_dBA: 68,
    capexBand: 'high', notes: 'Highest efficiency, lowest noise. Premium CAPEX. Best for large duty.' },
];

function isentropicPower_kW(q_Nm3h: number, depth_m: number, dropKpa: number, eff: number): number {
  const gamma = 1.4;
  const Q = q_Nm3h / 3600;
  const P_out = P_ATM + (RHO_W * G * depth_m + dropKpa * 1000) * 1.1;
  const exp = (gamma - 1) / gamma;
  return (gamma / (gamma - 1)) * P_ATM * Q * (Math.pow(P_out / P_ATM, exp) - 1) / eff / 1000;
}

function fmt(n: number, d = 1) { if (!isFinite(n)) return '—'; return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }); }

export default function BlowerSelectionPage() {
  const navigate = useNavigate();
  const [spec, setSpec] = useState<ModSpec>(() => ({ ...DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS) }));
  const [demand] = useState<SeasonalDemand[]>(DEFAULT_DEMAND);

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === KEYS.DIFFUSER_MODULE) setSpec({ ...DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS) });
    });
    return () => unsub();
  }, []);

  const qPerModule = useMemo(() => qPerModuleNm3h(specFromLegacy(spec)), [spec]);

  const seasons = useMemo(() => demand.map(d => {
    const q_Nm3h = qPerModule * d.modules * d.factor;
    const kw = isentropicPower_kW(q_Nm3h, spec.waterDepthM, spec.diffuserDropKpa ?? 1.5, 0.70);
    return { ...d, q_Nm3h, kw };
  }), [demand, qPerModule, spec]);

  const qMin = Math.min(...seasons.map(s => s.q_Nm3h));
  const qMax = Math.max(...seasons.map(s => s.q_Nm3h));
  const kwMax = Math.max(...seasons.map(s => s.kw));
  const turnDownNeeded = qMax / Math.max(1, qMin);

  const matches = useMemo(() => BLOWERS.map(b => {
    // Single blower must cover qMin..qMax via turn-down; if not, need >1 unit
    let nUnits = 1;
    let acceptable = false;
    for (let n = 1; n <= 4; n++) {
      const perUnitMin = b.qMin_Nm3h;
      const perUnitMax = b.qMax_Nm3h;
      // Stage on/off + turn-down: total turn-down = n × turnDown
      const totalMax = n * perUnitMax;
      const totalMin = perUnitMin;
      const td = totalMax / Math.max(1, totalMin);
      if (totalMax >= qMax * 1.05 && td >= turnDownNeeded && totalMin <= qMin) {
        nUnits = n;
        acceptable = true;
        break;
      }
    }
    // N+1 redundancy
    const nWithRedundancy = nUnits + 1;
    const kwAtQMax = isentropicPower_kW(qMax, spec.waterDepthM, spec.diffuserDropKpa ?? 1.5, b.efficiency);
    const annualKWh = isentropicPower_kW((qMin + qMax) / 2, spec.waterDepthM, spec.diffuserDropKpa ?? 1.5, b.efficiency) * 24 * 365 * 0.55;  // 55% duty
    return { b, nUnits, nWithRedundancy, acceptable, kwAtQMax, annualKWh, turnDownAvailable: nUnits * b.turnDownRatio };
  }), [qMin, qMax, turnDownNeeded, spec]);

  const bestMatch = matches
    .filter(m => m.acceptable)
    .sort((a, b) => b.b.efficiency - a.b.efficiency)[0];

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Wind}
        title="Blower Selection & VSD Turn-Down"
        subtitle="Match seasonal AOTR demand against candidate blower families with N+1 redundancy and turn-down headroom"
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      {/* Headline */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Demand range</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(qMin, 0)}–{fmt(qMax, 0)} Nm³/h</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Turn-down ratio needed</div>
          <div className="text-2xl font-bold text-amber-300 mt-1">{fmt(turnDownNeeded, 1)} : 1</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Peak shaft power (η 70%)</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(kwMax, 1)} kW</div>
        </div>
        {bestMatch && (
          <div className="rounded-xl border-2 border-emerald-700 bg-emerald-900/20 p-4">
            <div className="text-xs text-emerald-300 uppercase tracking-wider">Best match</div>
            <div className="text-sm font-bold text-emerald-200 mt-1">{bestMatch.b.label}</div>
            <div className="text-xs text-slate-400 mt-1">{bestMatch.nWithRedundancy} units (N+1) · η {(bestMatch.b.efficiency * 100).toFixed(0)} %</div>
          </div>
        )}
      </div>

      {/* Seasonal demand curve */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-slate-100">Seasonal demand</div>
          <button onClick={() => setSpec({ ...DEFAULTS, ...loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS) })}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-700">
            <RefreshCcw className="w-3.5 h-3.5" /> Reload session
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-2 py-1.5 text-left text-slate-300">Season</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Modules</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Factor</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Airflow (Nm³/h)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Shaft kW (η 70%)</th>
              </tr>
            </thead>
            <tbody>
              {seasons.map(s => (
                <tr key={s.label} className="border-b border-slate-800">
                  <td className="px-2 py-1 text-slate-100 font-semibold">{s.label}</td>
                  <td className="px-2 py-1 text-right text-slate-300">{s.modules}</td>
                  <td className="px-2 py-1 text-right text-slate-300">{s.factor.toFixed(2)}</td>
                  <td className="px-2 py-1 text-right text-slate-100 tabular-nums">{fmt(s.q_Nm3h, 0)}</td>
                  <td className="px-2 py-1 text-right text-slate-100 tabular-nums">{fmt(s.kw, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Candidate blowers */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-3">Candidate blowers (matched against demand)</div>
        <div className="space-y-3">
          {matches.map(m => (
            <div key={m.b.id} className={`rounded-xl border p-4 ${
              m.acceptable
                ? (m === bestMatch ? 'border-emerald-700 bg-emerald-900/15' : 'border-slate-700 bg-slate-900')
                : 'border-red-700 bg-red-900/10'
            }`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {m.acceptable
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      : <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />}
                    <div className="text-sm font-bold text-slate-100">{m.b.label}</div>
                    <span className="text-[10px] text-slate-400 uppercase">{m.b.technology}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      m.b.capexBand === 'low' ? 'bg-emerald-900/40 text-emerald-300' :
                      m.b.capexBand === 'mid' ? 'bg-amber-900/40 text-amber-300' :
                      'bg-red-900/40 text-red-300'
                    }`}>{m.b.capexBand.toUpperCase()} CAPEX</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">{m.b.notes}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs mt-3">
                <div><span className="text-slate-500">Unit q</span><br /><span className="text-slate-100">{m.b.qMin_Nm3h}–{m.b.qMax_Nm3h} Nm³/h</span></div>
                <div><span className="text-slate-500">Turn-down</span><br /><span className="text-slate-100">{m.b.turnDownRatio}:1</span></div>
                <div><span className="text-slate-500">Units (N+1)</span><br /><span className="text-slate-100 font-bold">{m.nWithRedundancy}</span></div>
                <div><span className="text-slate-500">Efficiency</span><br /><span className="text-slate-100">{(m.b.efficiency * 100).toFixed(0)} %</span></div>
                <div><span className="text-slate-500">Shaft kW peak</span><br /><span className="text-slate-100">{fmt(m.kwAtQMax, 1)}</span></div>
                <div><span className="text-slate-500">Est. annual kWh</span><br /><span className="text-slate-100">{fmt(m.annualKWh / 1000, 1)} MWh</span></div>
              </div>
              {!m.acceptable && (
                <div className="text-xs text-red-300 mt-2">
                  ⚠ Cannot match the {fmt(turnDownNeeded, 1)}:1 turn-down range with even 4 staged units. Consider an alternative blower family.
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Notes</div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            Sizing assumes isentropic compression with overall efficiency as quoted per family.
            Real-world wire-to-air efficiency varies with load, inlet temperature and back-pressure;
            the figures here are nominal for comparison.
          </p>
          <p>
            All scenarios include N+1 redundancy — one full standby unit beyond the duty count.
            Annual energy assumes 55 % duty cycle weighted across the seasonal demand curve. Final
            blower selection follows the pilot trial, the embankment power audit and a noise prediction
            against BS 4142:2014.
          </p>
        </div>
      </div>
    </div>
  );
}
